import {
    Component,
    OnDestroy,
    OnInit,
    ChangeDetectionStrategy,
} from '@angular/core';
import {
    FormControl,
    FormGroup,
    FormsModule,
    ReactiveFormsModule,
    Validators,
} from '@angular/forms';
import { MatFormFieldModule } from '@angular/material/form-field';
import { MatSelectModule } from '@angular/material/select';
import { DropdownOption } from '../../../../core/models/dropdown-option';
import { DropdownService } from '../../../../core/services/resources/dropdown.service';
import { forkJoin, map, Observable, switchMap, takeUntil, tap } from 'rxjs';
import { FrnFilingTypeResponse } from './models/frn-filing-type-response';
import { FrnFilingTypeRequest } from './models/frn-filing-type-request';
import { FrnFilingCreationRequest } from '../shared/models/frn-filing-creation-request';
import { ActivatedRoute, Router } from '@angular/router';
import { FormGroupType } from '../../../../core/models/form-group-type';
import { StepValidationResult } from '../../shared/models/step-validation-result';
import { FrnFilingPageBaseComponent } from '../shared/components/frn-filing-page-base/frn-filing-page-base.component';
import { ApplicationStateService } from '../../shared/services/application-state/application-state.service';
import { ApplicationFormControlManagerService } from '../../shared/services/application-form-control-manager/application-form-control-manager.service';
import { StepNavigationService } from '../../shared/services/step-navigation/step-navigation.service';
import { FormEntryService } from '../../../../core/services/form-entry/form-entry.service';
import { FrnFilingService } from '../shared/services/frn-filing.service';
import { ApplicationService } from '../../shared/services/application/application.service';
import { MatDialog } from '@angular/material/dialog';
import { ReturnRouteService } from '../../../../core/services/navigation/return-route.service';

@Component({
    selector: 'app-frn-filing-type',
    templateUrl: './frn-filing-type.component.html',
    changeDetection: ChangeDetectionStrategy.Eager,
    imports: [
        FormsModule,
        ReactiveFormsModule,
        MatFormFieldModule,
        MatSelectModule,
    ],
})
export class FrnFilingTypeComponent
    extends FrnFilingPageBaseComponent<
        FrnFilingTypeRequest,
        FrnFilingTypeResponse,
        FrnFilingTypeRequest
    >
    implements OnInit, OnDestroy
{
    form = new FormGroup({
        coresRegistrationType: new FormControl<string | null>(
            null,
            Validators.required,
        ),
        hasUsaContactAddress: new FormControl<boolean | null>(
            null,
            Validators.required,
        ),
    }) as FormGroup<FormGroupType<FrnFilingTypeRequest>>;

    yesNoOptions: DropdownOption[] = [];
    coresRegistrationTypeOptions: DropdownOption[] = [];

    constructor(
        protected override readonly route: ActivatedRoute,
        protected override readonly stepService: StepNavigationService,
        protected override readonly router: Router,
        protected override readonly formEntryService: FormEntryService,
        protected override readonly applicationStateService: ApplicationStateService,
        protected override readonly applicationFormControlManagerService: ApplicationFormControlManagerService,
        protected override readonly frnFilingService: FrnFilingService,
        protected override readonly dialog: MatDialog,
        protected override readonly returnRouteService: ReturnRouteService,
        private readonly dropdownService: DropdownService,
        private readonly applicationService: ApplicationService,
    ) {
        super(
            route,
            stepService,
            router,
            formEntryService,
            applicationStateService,
            applicationFormControlManagerService,
            frnFilingService,
            dialog,
            returnRouteService,
        );
    }

    override ngOnInit(): void {
        super.ngOnInit();
        this.loadDropdownData();
    }

    private loadDropdownData(): void {
        forkJoin({
            yesNoOptions: this.dropdownService.getYesNoOptions(),
            coresRegistrationTypeOptions:
                this.dropdownService.getCoresRegistrationTypeOptions(),
        })
            .pipe(
                takeUntil(this.destroy$),
                tap(({ yesNoOptions, coresRegistrationTypeOptions }) => {
                    this.yesNoOptions = yesNoOptions;
                    this.coresRegistrationTypeOptions =
                        coresRegistrationTypeOptions;
                }),
            )
            .subscribe();
    }

    protected getFormDataFromApi(
        applicationId: string,
    ): Observable<FrnFilingTypeResponse> {
        return this.frnFilingService.getFrnFilingType(applicationId);
    }

    protected saveFormDataToApi(
        request: FrnFilingTypeRequest,
    ): Observable<StepValidationResult | null> {
        // New FRN created from side nav
        if (!this.applicationId) {
            const createRequest: FrnFilingCreationRequest = {
                ...request,
                parentApplicationId: null,
            };

            return this.frnFilingService.createFrnFiling(createRequest).pipe(
                tap((response) => {
                    this.applicationId = response.applicationId.toString();
                }),
                // After creation, fetch and set application state
                switchMap(() =>
                    this.applicationService.getApplicationState(
                        this.applicationId!,
                    ),
                ),
                // Then initialize steps and set validations
                switchMap(() =>
                    this.frnFilingService.getAndSetFrnStepsWithValidations(
                        this.applicationId!,
                        true,
                        false,
                    ),
                ),
                map(() => null),
            );
        }

        // Existing FRN so we just update normally
        return this.frnFilingService.updateFrnFilingType(
            this.applicationId,
            request,
        );
    }

    protected bindForm(response: FrnFilingTypeResponse): void {
        this.form.patchValue(response);
    }

    protected generateRequestObject(): FrnFilingTypeRequest {
        return this.form.getRawValue() as FrnFilingTypeRequest;
    }
}
